
import { useCallback, useMemo, useSyncExternalStore } from 'react'
import { isCrossingStyle, type CrossingStyle } from '@/lib/cable-route'

/**
 * Per-browser conveniences (recently placed devices, list density) live in
 * localStorage. The hook is an external store rather than an effect: the
 * server snapshot is null, so the first client render matches the server
 * markup (no hydration warning), and nothing calls setState inside an effect
 * (the React Compiler lint rule forbids it). `initial` and `validate` should
 * be module-level constants so the parsed value is only recomputed on change.
 */

const listeners = new Map<string, Set<() => void>>()

function notify(key: string) {
  listeners.get(key)?.forEach((listener) => listener())
}

function read(key: string): string | null {
  try {
    return window.localStorage.getItem(key)
  } catch {
    return null
  }
}

function write(key: string, value: string | null) {
  try {
    if (value === null) window.localStorage.removeItem(key)
    else window.localStorage.setItem(key, value)
  } catch {
    // Private mode or a full quota: the in-memory value still updates below.
  }
}

function parse<T>(raw: string | null, initial: T, validate: (value: unknown) => T | null): T {
  if (raw === null) return initial
  try {
    return validate(JSON.parse(raw)) ?? initial
  } catch {
    return initial
  }
}

export function useLocalStorage<T>(
  key: string,
  initial: T,
  validate: (value: unknown) => T | null,
): [T, (next: T | ((current: T) => T)) => void] {
  const subscribe = useCallback(
    (listener: () => void) => {
      const set = listeners.get(key) ?? new Set()
      set.add(listener)
      listeners.set(key, set)
      const onStorage = (event: StorageEvent) => {
        if (event.key === null || event.key === key) listener()
      }
      window.addEventListener('storage', onStorage)
      return () => {
        set.delete(listener)
        window.removeEventListener('storage', onStorage)
      }
    },
    [key],
  )
  const raw = useSyncExternalStore(subscribe, () => read(key), () => null)
  const value = useMemo(() => parse(raw, initial, validate), [raw, initial, validate])

  const set = useCallback(
    (next: T | ((current: T) => T)) => {
      const current = parse(read(key), initial, validate)
      const resolved = typeof next === 'function' ? (next as (current: T) => T)(current) : next
      write(key, JSON.stringify(resolved))
      notify(key)
    },
    [key, initial, validate],
  )

  return [value, set]
}

/* ------------------------------- keys -------------------------------- */

export const RECENT_KEY = (workspaceSlug: string) => `routr:recent:${workspaceSlug}`
export const RECENT_MAX = 8
export const DENSITY_KEY = 'routr:library-density'

export type Density = 'comfortable' | 'compact'

export const EMPTY_STRINGS: string[] = []

export const stringList = (value: unknown): string[] | null =>
  Array.isArray(value) && value.every((item) => typeof item === 'string') ? (value as string[]) : null

export const densityValue = (value: unknown): Density | null =>
  value === 'compact' || value === 'comfortable' ? value : null

/** Most recent first, no duplicates, capped. */
export function pushRecent(list: readonly string[], id: string, max = RECENT_MAX): string[] {
  return [id, ...list.filter((item) => item !== id)].slice(0, max)
}

/* ---------------------------- canvas panels --------------------------- */

/** Which side panels of the routing canvas are open. One key for both, per browser. */
export const PANELS_KEY = 'routr:canvas-panels'

export type PanelPrefs = { library: boolean; details: boolean }

export const DEFAULT_PANELS: PanelPrefs = { library: true, details: true }

export const panelsValue = (value: unknown): PanelPrefs | null =>
  value !== null &&
  typeof value === 'object' &&
  typeof (value as PanelPrefs).library === 'boolean' &&
  typeof (value as PanelPrefs).details === 'boolean'
    ? { library: (value as PanelPrefs).library, details: (value as PanelPrefs).details }
    : null

/* ---------------------------- cable arrows ---------------------------- */

/** Whether cables carry an arrowhead at the input end. On unless switched off. */
export const ARROWS_KEY = 'routr:canvas-arrows'

export const boolValue = (value: unknown): boolean | null => (typeof value === 'boolean' ? value : null)

/* --------------------------- cable crossings -------------------------- */

/** How a cable is drawn where it crosses another. Off unless chosen. */
export const CROSSINGS_KEY = 'routr:canvas-crossings'

export const crossingValue = (value: unknown): CrossingStyle | null => (isCrossingStyle(value) ? value : null)
